import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Trash, Settings } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ProductAttribute } from "@/types/product-type";
import { getAttributeIcon } from "@/constants/product-attributes";

interface ProductAttributeCardProps {
  attribute: ProductAttribute;
  onEdit: (attribute: ProductAttribute) => void;
  onDelete: (id: string) => void;
  onManageValues: (attributeId: string) => void;
}

export const ProductAttributeCard = ({
  attribute,
  onEdit,
  onDelete,
  onManageValues,
}: ProductAttributeCardProps) => {
  const { IconComponent, className } = getAttributeIcon(attribute.name);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-4 bg-gray-50 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <IconComponent className={className} />
            <CardTitle className="ml-2 text-lg">
              {attribute.displayName || attribute.name}
            </CardTitle>
          </div>
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(attribute)}
              className="hover:bg-gray-100"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-gray-100"
                >
                  <Trash className="h-4 w-4 text-red-500" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Attribute</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this attribute? This action
                    will also delete all associated values.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={() => onDelete(attribute.id)}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="mb-4">
          <div className="text-sm text-gray-500">System Name</div>
          <div className="font-mono text-sm">{attribute.name}</div>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm">
            <span className="text-gray-500 mr-2">Status:</span>
            <span
              className={attribute.isActive ? "text-green-600" : "text-red-600"}
            >
              {attribute.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onManageValues(attribute.id)}
            className="hover:bg-gray-50"
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Values
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
